/**
 * Date and Time Formatting Utilities
 * Provides consistent date/time formatting across the entire application
 */

/**
 * Parse a date string without timezone issues
 * @param {string} dateString - Date in Y-m-d format (e.g., "2024-12-31")
 * @returns {Date} JavaScript Date object
 */
export const parseLocalDate = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Format date for display (Hebrew locale)
 * @param {string|Date} date - Date string (Y-m-d) or Date object
 * @param {boolean} includeWeekday - Whether to include day of week
 * @returns {string} Formatted date in Hebrew (e.g., "יום חמישי, 31 בדצמבר 2024")
 */
export const formatDate = (date, includeWeekday = false) => {
  const dateObj = typeof date === 'string' ? parseLocalDate(date) : date;

  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  if (includeWeekday) {
    options.weekday = 'long';
  }

  return dateObj.toLocaleDateString('he-IL', options);
};

/**
 * Format date for display (short format)
 * @param {string|Date} date - Date string (Y-m-d) or Date object
 * @returns {string} Formatted date in Hebrew short format (e.g., "31.12.2024")
 */
export const formatDateShort = (date) => {
  const dateObj = typeof date === 'string' ? parseLocalDate(date) : date;
  return dateObj.toLocaleDateString('he-IL');
};

/**
 * Format time for display (24-hour format)
 * @param {string} time - Time string (H:i or H:i:s format, e.g., "14:30" or "14:30:00")
 * @returns {string} Formatted time in 24-hour format (e.g., "14:30")
 */
export const formatTime = (time) => {
  // If time already in correct format (HH:MM), return as is
  if (time && time.length >= 5) {
    return time.substring(0, 5);
  }
  return time;
};

/**
 * Format time from a Date object
 * @param {Date} date - JavaScript Date object
 * @returns {string} Formatted time in 24-hour format (e.g., "14:30")
 */
export const formatTimeFromDate = (date) => {
  return date.toLocaleTimeString('he-IL', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * Format full date and time for display
 * @param {string} date - Date string (Y-m-d)
 * @param {string} time - Time string (H:i or H:i:s)
 * @returns {string} Formatted date and time (e.g., "31.12.2024 בשעה 14:30")
 */
export const formatDateTime = (date, time) => {
  return `${formatDateShort(date)} בשעה ${formatTime(time)}`;
};

/**
 * Get day of week in Hebrew
 * @param {string|Date} date - Date string (Y-m-d) or Date object
 * @returns {string} Day of week in Hebrew (e.g., "יום חמישי")
 */
export const getDayOfWeek = (date) => {
  const dateObj = typeof date === 'string' ? parseLocalDate(date) : date;
  return dateObj.toLocaleDateString('he-IL', { weekday: 'long' });
};

/**
 * Check if a date is in the past
 * @param {string} date - Date string (Y-m-d)
 * @param {string} time - Optional time string (H:i or H:i:s)
 * @returns {boolean} True if date/time is in the past
 */
export const isPastDateTime = (date, time = null) => {
  const now = new Date();

  if (time) {
    const dateTime = new Date(`${date}T${time}`);
    return dateTime < now;
  } else {
    const dateObj = parseLocalDate(date);
    dateObj.setHours(23, 59, 59, 999); // End of day
    return dateObj < now;
  }
};

/**
 * Check if subscription is active
 * @param {string} endDate - Subscription end date (Y-m-d)
 * @returns {boolean} True if subscription is still active
 */
export const isSubscriptionActive = (endDate) => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const endDateObj = parseLocalDate(endDate);
  endDateObj.setHours(23, 59, 59, 999);

  return endDateObj >= currentDate;
};

/**
 * Get time difference in hours
 * @param {string} date - Date string (Y-m-d)
 * @param {string} time - Time string (H:i or H:i:s)
 * @returns {number} Hours until/since the date/time (negative if in past)
 */
export const getHoursUntil = (date, time) => {
  // Parse date and time components to avoid timezone issues
  const [year, month, day] = date.split('-').map(Number);
  const timeParts = time.split(':').map(Number);
  const hours = timeParts[0];
  const minutes = timeParts[1] || 0;
  const seconds = timeParts[2] || 0;

  // Create date in local timezone
  const dateTime = new Date(year, month - 1, day, hours, minutes, seconds);
  const now = new Date();

  const diff = dateTime - now;
  return diff / (1000 * 60 * 60);
};

/**
 * Format date for input field (Y-m-d format)
 * @param {Date} date - JavaScript Date object
 * @returns {string} Date string in Y-m-d format (e.g., "2024-12-31")
 */
export const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
